import {
  useRegisterPushMutation,
  useRegisterPushWebMutation
} from '@shared/api/requests';
import { getToken, onMessage, type Messaging } from 'firebase/messaging';
import { useEffect, useState } from 'react';

import { getMessagingIfSupported } from '@/firebase';
import { useProfileStore } from '@/shared/store/profileStore';
import {
  getPlatformInfo,
  getPushPlatform
} from '@/shared/utils/platformDetection';

const VAPID_PUBLIC_KEY = import.meta.env.VITE_WEB_PUSH_PUBLIC_KEY;

const urlBase64ToUint8Array = (base64String: string) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return new Uint8Array([...rawData].map((char) => char.charCodeAt(0)));
};

export const usePushNotifications = () => {
  const { isAuthenticated } = useProfileStore();
  const registerMutation = useRegisterPushMutation();
  const registerWebPushMutation = useRegisterPushWebMutation();
  const [messaging, setMessaging] = useState<Messaging | null>(null);

  useEffect(() => {
    if (isAuthenticated) {
      getMessagingIfSupported().then(setMessaging);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!messaging) return;

    const unsub = onMessage(messaging, (payload) => {
      if (payload.notification) {
        new Notification(payload.notification.title || '', {
          body: payload.notification.body,
          data: payload.data,
          icon: '/pwa-192x192.png'
        });
      }
    });

    return () => unsub();
  }, [messaging]);

  const requestPermissionAndRegister = async () => {
    if (!isAuthenticated || !('Notification' in window)) return;

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return;

    const platformInfo = getPlatformInfo();

    try {
      if (platformInfo.isApplePlatform) {
        // if (platformInfo.isApplePlatform) {
        // if ('PushManager' in window) {

        // if (true) {
        //   macOS/iOS → Web Push
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
          console.warn('Web Push not supported');
          return;
        }

        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
        });

        console.log('[WEB PUSH] subscription:', subscription);

        // await fetch('https://api.lbconnect.me/api/push/register-web', {
        //   method: 'POST',
        //   headers: { 'Content-Type': 'application/json' },
        //   body: JSON.stringify({
        //     userId: '123', // можно заменить на реальный ID
        //     subscription,
        //     platform: 'webpush'
        //   })
        // });

        // Формируем тело запроса, как ожидает бэкенд:
        const body = {
          userId: '123', // заменить на реальный userId
          endpoint: subscription.endpoint,
          p256dh: subscription.getKey
            ? btoa(
                String.fromCharCode(
                  ...new Uint8Array(subscription.getKey('p256dh')!)
                )
              )
            : '',
          auth: subscription.getKey
            ? btoa(
                String.fromCharCode(
                  ...new Uint8Array(subscription.getKey('auth')!)
                )
              )
            : '',
          platform: 'webpush'
        };

        console.log('[WEB PUSH] Body for backend:', body);
        await registerWebPushMutation.mutateAsync({
          params: body
        });
      } else {
        // Android, Windows → Firebase
        if (!messaging) return;

        const registration = await navigator.serviceWorker.ready;
        const token = await getToken(messaging, {
          serviceWorkerRegistration: registration
        });
        if (token) {
          console.log('[FCM TOKEN] ', token);

          // await fetch('https://api.lbconnect.me/api/push/register', {
          //   method: 'POST',
          //   headers: { 'Content-Type': 'application/json' },
          //   body: JSON.stringify({
          //     token,
          //     platform: getPushPlatform()
          //   })
          // });

          await registerMutation.mutateAsync({
            params: {
              token,
              platform: getPushPlatform()
            }
          });
        }
      }
    } catch (err) {
      console.error('Push notification registration failed', err);
    }
  };

  return { requestPermissionAndRegister };
};
